import React, { useRef, useCallback, useMemo } from 'react';
import warning from 'warning';
import { isVertical } from './utils';
import { TabBarTabsNodeProps } from './iTabs'
import classnames from 'classnames';

// 使用 ref 跟踪拖拽状态，避免不必要的重渲染
interface DragInfo {
    draggedIndex: number | null;
    isDragging: boolean;
    lastUpdate: number;
}

const TabBarTabsNode: React.FC<TabBarTabsNodeProps> = (props) => {
    const {
        panels: children = [],
        activeKey,
        clsPrefix = [],
        tabBarGutter = null,
        saveRef = () => {},
        tabBarPosition,
        renderTabBarNode,
        dir: direction,
        dragable,
        fieldid,
        type,
        items,
        onTabClick = () => {},
        onDrag
    } = props;

    // 使用 ref 跟踪拖拽状态，避免不必要的重渲染
    const dragInfoRef = useRef<DragInfo>({
        draggedIndex: null,
        isDragging: false,
        lastUpdate: 0
    });

    const draggedElementRef = useRef<HTMLElement | null>(null);
    const handleTabClick = useCallback((key: string) => {
        onTabClick?.(key);
    }, [onTabClick]);
    const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
        const element = e.currentTarget as HTMLElement;
        draggedElementRef.current = element;

        // 更新拖拽信息（使用 ref 而不是 state）
        dragInfoRef.current = {
            draggedIndex: index,
            isDragging: true,
            lastUpdate: Date.now()
        };

        // 设置拖拽数据
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', element.innerHTML);

        // 直接设置拖拽样式
        requestAnimationFrame(() => {
            if (draggedElementRef.current) {
                draggedElementRef.current.style.opacity = '0.4';
            }
        });
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        const dragInfo = dragInfoRef.current;
        const now = Date.now();

        // 节流更新，每 50ms 最多一次
        if (dragInfo.draggedIndex !== null &&
            dragInfo.draggedIndex !== index &&
            now - dragInfo.lastUpdate > 50) {

            dragInfoRef.current.lastUpdate = now;

            // 使用 data 属性更新视觉效果，避免 setState
            const allTabs = document.querySelectorAll('[data-dragover]');
            allTabs.forEach(el => el.removeAttribute('data-dragover'));
            e.currentTarget.setAttribute('data-dragover', 'true');
        }
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        // handleDragOver 已经处理了视觉反馈，这里只需阻止默认行为
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        // 使用 dataset 移除视觉反馈
        delete e.currentTarget.dataset.dragOver;
    }, []);

    const handleDragEnd = useCallback(() => {
        // 恢复拖拽元素的样式
        if (draggedElementRef.current) {
            draggedElementRef.current.style.opacity = ''; // 恢复为默认值
        }

        // 清理所有拖拽样式
        requestAnimationFrame(() => {
            const elements = document.querySelectorAll('[data-dragover]');
            elements.forEach(el => el.removeAttribute('data-dragover'));
        });

        // 重置 ref 状态
        dragInfoRef.current = {
            draggedIndex: null,
            isDragging: false,
            lastUpdate: 0
        };
        draggedElementRef.current = null;
    }, []);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
        e.preventDefault();
        e.stopPropagation();

        // 移除拖拽样式
        delete e.currentTarget.dataset.dragOver;

        const dragInfo = dragInfoRef.current;
        if (dragInfo.draggedIndex !== null && dragInfo.draggedIndex !== dropIndex) {
            // 构造与 react-beautiful-dnd 相似的 result 对象
            const result = {
                source: {
                    index: dragInfo.draggedIndex,
                    droppableId: 'droppable'
                },
                destination: {
                    index: dropIndex,
                    droppableId: 'droppable'
                },
                draggableId: `tab_${dragInfo.draggedIndex}`,
                type: 'DEFAULT'
            };

            // 调用回调
            if (onDrag) {
                onDrag(result);
            }
        }

        handleDragEnd();
    }, [onDrag, handleDragEnd]);
    const iseditable = useCallback((v: undefined | boolean) => {
        if (type == 'editable-card') {
            if (v === undefined || v == true) {
                return
            } else {
                return 'noClose'
            }
        }
        return
    }, [type]);

    // 抽取的公共渲染函数，用于生成单个tab项
    const renderSingleTab = useCallback((
        key: string,
        tab: any,
        disabled: boolean | undefined,
        index: number,
        extraProps: any = {}
    ) => {
        const isActive = activeKey === key;
        const events = disabled ? {} : { onClick: () => handleTabClick(key) };
        const ref = isActive ? { ref: saveRef!('activeTab') } : {};
        const gutter = tabBarGutter && extraProps.isLastItem ? 0 : tabBarGutter;
        const marginProperty = direction === 'rtl' ? 'marginLeft' : 'marginRight';
        const style = { [isVertical(tabBarPosition) ? 'marginBottom' : marginProperty]: gutter };
        let child = React.isValidElement(tab) ? (tab.props as { children?: React.ReactNode }).children : tab;
        let children = child?.props?.children;
        let isFillLineActive = !tab?.props?.className || (tab?.props?.className && !tab?.props?.className.includes("wui-tabs-fill-line-active"))
        const commonProps = {
            role: "tab",
            'aria-disabled': disabled ? 'true' : 'false',
            'aria-selected': isActive ? 'true' : 'false',
            ...events,
            className: classnames({
                [`${clsPrefix}-tab-active`]: isActive,
                [`${clsPrefix}-tab`]: true,
                [`${clsPrefix}-tab-disabled`]: disabled,
                [`${clsPrefix}-tab-line-active`]: type === 'simple' && isActive && typeof tab !== 'function',
                [`${clsPrefix}-tab-fill-line-active`]: type === 'fill-line' && isActive && (typeof child == 'string' || typeof children == 'string' || typeof children == 'object') && isFillLineActive,
                [`${clsPrefix}-tab-trangle-active`]: type === 'trangle' && isActive && typeof tab !== 'function'
            }),
            key,
            style,
            ...ref,
            ...extraProps
        };

        // 拖拽属性
        const dragProps = dragable && !disabled ? {
            draggable: true,
            onDragStart: (e: React.DragEvent<HTMLDivElement>) => handleDragStart(e, index),
            onDragOver: (e: React.DragEvent<HTMLDivElement>) => handleDragOver(e, index),
            onDragEnter: handleDragEnter,
            onDragLeave: handleDragLeave,
            onDrop: (e: React.DragEvent<HTMLDivElement>) => handleDrop(e, index),
            onDragEnd: handleDragEnd
        } : {};

        // 处理tab内容
        let tabContent = tab;
        if (typeof tab === 'function') {
            // 注意：保留这个div参数是为了兼容性，某些旧代码可能依赖它
            tabContent = tab(<div className="wui-tabs-text-content"></div>, key);
        }

        return (
            <div {...commonProps} {...dragProps}>
                {tabContent}
            </div>
        );
    }, [activeKey, clsPrefix, type, handleTabClick, saveRef,
        tabBarGutter, direction, tabBarPosition, dragable,
        handleDragStart, handleDragOver, handleDragEnter, handleDragLeave, handleDrop, handleDragEnd]);
    // 使用 useMemo 缓存标签项的渲染结果
    const tabItems = useMemo(() => {
        let rst: any[] = [];
        // type为editable-card模式时不能直接使用items，内部包含关闭图标逻辑在tas层生成React.cloneElement元素，走原逻辑
        if (items && items?.length > 0 && type != 'editable-card') {
            items?.map((item, index) => {
                const key = item.key;
                warning('tab' in item, 'There must be `tab` property on children of Tabs.');

                // 使用公共渲染函数
                const extraProps = {
                    nid: item.nid,
                    uitype: item.uitype,
                    // @ts-ignore
                    nodekey: key,
                    closable: iseditable(item.closable),
                    id: item?.id,
                    fieldid: item?.fieldid ? item?.fieldid : fieldid ? fieldid + '-tabs-area-' + index : undefined,
                    isLastItem: index === items.length - 1
                };

                let node = renderSingleTab(key, item.tab, item.disabled, index, extraProps);

                if (renderTabBarNode) {
                    node = renderTabBarNode(node) as React.ReactElement;
                }

                rst.push(node);
            })
        } else {
            React.Children.forEach(children, (child: React.ReactElement, index) => {
                if (!child) return;
                const key = child.key || '';
                warning('tab' in child.props, 'There must be `tab` property on children of Tabs.');

                // 使用公共渲染函数
                const childrenArray = React.Children.toArray(children);
                const extraProps = {
                    nid: child.props.nid,
                    uitype: child.props.uitype,
                    // @ts-ignore
                    nodekey: key,
                    closable: iseditable(child.props.closable),
                    id: child.props?.id,
                    fieldid: child.props?.fieldid ? child.props?.fieldid : fieldid ? fieldid + '-tabs-area-' + index : undefined,
                    isLastItem: index === childrenArray.length - 1
                };

                let node = renderSingleTab(key, child.props.tab, child.props.disabled, index, extraProps);

                if (renderTabBarNode) {
                    node = renderTabBarNode(node) as React.ReactElement;
                }

                rst.push(node);
            });
        }
        return rst
    }, [children, items, type, fieldid, renderTabBarNode, renderSingleTab, iseditable]);

    return (
        <div ref={saveRef!('navTabsContainer') as React.LegacyRef<HTMLDivElement>}>
            {tabItems}
        </div>
    );
};

export default TabBarTabsNode;
